import { saveRecordEntry, readRecordEntry, enqueueSubmission } from "./db";
import type { SubmissionQueueEntry } from "./db";
import { isRuntimeOfflineEnabled } from "./runtime";
import { OfflineCacheMissError, OfflineQueueError, OfflineRequestQueuedError } from "./errors";

export type OfflineRequestContext = {
  path: string;
  url: string;
  method: string;
  requestInit: RequestInit;
};

type PrepareResult<T> =
  | { kind: "network"; requestInit?: RequestInit }
  | { kind: "cached"; data: T }
  | { kind: "queued"; queueEntry: SubmissionQueueEntry & { id: number } };

const HTTP_CACHE_RESOURCE = "http-cache";

const isNavigatorOnline = () => {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine;
};

const serialiseBody = (body: BodyInit | null | undefined): unknown => {
  if (!body) return undefined;
  if (typeof body === "string") return body;
  if (body instanceof Blob) {
    return body;
  }
  if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
    return body;
  }
  throw new OfflineQueueError("Unsupported request body type for offline queueing. Ensure requests send JSON payloads.");
};

export class OfflineHttpAdapter {
  async prepare<T>(context: OfflineRequestContext): Promise<PrepareResult<T>> {
    const offlineEnabled = isRuntimeOfflineEnabled();
    const method = context.method.toUpperCase();
    const online = isNavigatorOnline();
    const offlineActive = offlineEnabled || !online;

    if (!offlineActive) {
      return { kind: "network" };
    }

    if (!offlineEnabled && online) {
      return { kind: "network" };
    }

    if (method === "GET") {
      const cached = await readRecordEntry<T>(HTTP_CACHE_RESOURCE, context.path);
      if (!cached) {
        throw new OfflineCacheMissError(`No cached response available for ${context.path}`);
      }
      return { kind: "cached", data: cached.data as T };
    }

    const body = serialiseBody(context.requestInit.body ?? undefined);
    const headers = context.requestInit.headers;
    const headerRecord =
      headers && typeof headers === "object" && "forEach" in headers
        ? (() => {
            const out: Record<string, string> = {};
            (headers as Headers).forEach((value, key) => {
              out[key] = value;
            });
            return out;
          })()
        : (headers as Record<string, string> | undefined);

    const entry = await enqueueSubmission({
      resource: HTTP_CACHE_RESOURCE,
      endpoint: context.path,
      method,
      body,
      headers: headerRecord,
      createdAt: Date.now(),
      status: "pending",
    });
    return { kind: "queued", queueEntry: entry };
  }

  async onSuccess<T>(context: OfflineRequestContext, response: T): Promise<void> {
    const offlineEnabled = isRuntimeOfflineEnabled();
    if (!offlineEnabled) return;
    const method = context.method.toUpperCase();
    if (method === "GET") {
      await saveRecordEntry({
        resource: HTTP_CACHE_RESOURCE,
        recordId: context.path,
        data: response,
        updatedAt: Date.now(),
      });
    }
  }

  async onError<T>(context: OfflineRequestContext, error: unknown): Promise<T | undefined> {
    const offlineEnabled = isRuntimeOfflineEnabled();
    if (!offlineEnabled) return undefined;
    const method = context.method.toUpperCase();

    if (method === "GET") {
      const cached = await readRecordEntry<T>(HTTP_CACHE_RESOURCE, context.path);
      if (cached) {
        return cached.data as T;
      }
    }
    throw error;
  }
}
